export type ErrorType =
  | 'bad_request'
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'rate_limit'
  | 'quota'
  | 'internal_server'
  | 'offline'
  | 'billing';

export type ErrorSurface =
  | 'chat'
  | 'auth'
  | 'api'
  | 'stream'
  | 'database'
  | 'history'
  | 'vote'
  | 'document'
  | 'suggestions'
  | 'user'
  | 'admin'
  | 'email'
  | 'snippets'
  | 'subscription'
  | 'webhooks'
  | 'models'
  | 'apps'
  | 'topup';

export type BillingErrorReason =
  | 'subscription_inactive'
  | 'free_quota_exceeded'
  | 'credit_limit_reached'
  | 'subscription_required'
  | 'insufficient_credits';

export type ErrorCode =
  | `${Exclude<ErrorType, 'billing'>}:${ErrorSurface}`
  | `billing:${BillingErrorReason}`;

export type ErrorVisibility = 'response' | 'log';

const VISIBILITY_CONFIG: Record<string, ErrorVisibility> = {
  database: 'log',
};

function getVisibility(surface: ErrorSurface | BillingErrorReason): ErrorVisibility {
  return VISIBILITY_CONFIG[surface] || 'response';
}

function getStatusCodeByType(type: ErrorType): number {
  switch (type) {
    case 'bad_request': return 400;
    case 'unauthorized': return 401;
    case 'billing': return 402;
    case 'forbidden': return 403;
    case 'not_found': return 404;
    case 'conflict': return 409;
    case 'quota': return 422;
    case 'rate_limit': return 429;
    case 'offline': return 503;
    case 'internal_server': return 500;
    default: return 500;
  }
}

const ERROR_MESSAGES: Record<ErrorCode, string> = {
  // Billing
  'billing:subscription_inactive': 'Your subscription is not active. Please update your payment method or reactivate your subscription.',
  'billing:free_quota_exceeded': 'You have exceeded your free plan quota. Please upgrade your plan.',
  'billing:credit_limit_reached': 'You have used all your monthly credits. They will refresh at the start of your next billing cycle.',
  'billing:subscription_required': 'Purchase credits to continue using FillApp.',
  'billing:insufficient_credits': 'Insufficient credits available. Please purchase more credits or upgrade your plan.',

  // Auth
  'unauthorized:auth': 'You need to sign in before continuing.',
  'forbidden:auth': 'Your account does not have access to this feature.',

  // Chat
  'internal_server:chat': 'An unexpected error occurred while processing your chat request.',
  'not_found:chat': 'The requested chat was not found.',
  'forbidden:chat': 'This chat belongs to another user.',
  'internal_server:history': 'An unexpected error occurred while loading your chat history.',

  // Database
  'internal_server:database': 'An error occurred while accessing the database.',

  // Admin
  'internal_server:admin': 'An internal error occurred in an admin process.',

  // Default/Fallback - It's better to have explicit messages.
  'bad_request:api': "The request couldn't be processed. Please check your input and try again.",
  'unauthorized:api': 'You are not authorized to perform this action.',
  'forbidden:api': 'You do not have permission to access this resource.',
  'not_found:api': 'The requested resource was not found.',
  'internal_server:api': 'An unexpected internal server error occurred.',
  'bad_request:user': "The user-related request couldn't be processed.",
  'not_found:user': 'User not found.',
  'internal_server:user': 'An unexpected error occurred while processing the user request.',
  'internal_server:subscription': 'An unexpected error occurred with the subscription service.',
  'bad_request:subscription': 'The subscription request was invalid.',
  'not_found:snippets': 'Snippet not found.',
  'internal_server:snippets': 'An unexpected error occurred with the snippet service.',
  'bad_request:snippets': 'The snippet request was invalid.',
  'conflict:snippets': 'A snippet with this name already exists.',
  'quota:snippets': 'You have reached the maximum number of snippets allowed.',
  'conflict:chat': 'A conflict occurred with this chat request.',
  'conflict:auth': 'An authentication conflict occurred.',
  'conflict:api': 'A conflict occurred with the API request.',
  'conflict:stream': 'A conflict occurred with the stream.',
  'conflict:database': 'A database conflict occurred.',
  'conflict:history': 'A conflict occurred with the history.',
  'conflict:vote': 'A voting conflict occurred.',
  'conflict:document': 'A document conflict occurred.',
  'conflict:suggestions': 'A suggestions conflict occurred.',
  'conflict:user': 'A user conflict occurred.',
  'conflict:admin': 'An admin conflict occurred.',
  'conflict:email': 'An email conflict occurred.',
  'conflict:subscription': 'A subscription conflict occurred.',
  'conflict:webhooks': 'A webhook conflict occurred.',
  'forbidden:vote': 'You are not authorized to vote on this message.',
  'unauthorized:vote': 'You must be signed in to vote.',
  'internal_server:vote': 'An unexpected error occurred while voting.',
  'not_found:document': 'The requested document was not found.',
  'forbidden:document': 'This document belongs to another user.',
  'unauthorized:document': 'You need to sign in to view this document.',
  'bad_request:document': 'The request to create or update the document was invalid.',
  'offline:stream': 'A network error occurred while streaming data.',
  'internal_server:stream': 'An internal error occurred during streaming.',
  'rate_limit:api': 'You have exceeded the rate limit. Please try again later.',
  'rate_limit:chat': 'You have exceeded your maximum number of messages for the day. Please try again later.',
  'bad_request:auth': 'The authentication request was invalid.',
  'internal_server:auth': 'An unexpected error occurred during authentication.',
  'not_found:auth': 'Authentication resource not found.',
  'offline:api': 'The service is currently offline. Please try again later.',
  'offline:auth': 'Authentication service is currently unavailable.',
  'offline:chat': 'Chat service is currently unavailable.',
  'offline:database': 'Database is currently unavailable.',
  'offline:document': 'Document service is currently unavailable.',
  'offline:history': 'History service is currently unavailable.',
  'offline:snippets': 'Snippets service is currently unavailable.',
  'offline:subscription': 'Subscription service is currently unavailable.',
  'offline:user': 'User service is currently unavailable.',
  'offline:vote': 'Vote service is currently unavailable.',
  'offline:webhooks': 'Webhook service is currently unavailable.',
  'offline:admin': 'Admin service is currently unavailable.',
  'offline:email': 'Email service is currently unavailable.',
  'bad_request:email': 'The email request was invalid.',
  'internal_server:email': 'An internal error occurred while processing the email request.',
  'not_found:email': 'Email resource not found.',
  'bad_request:webhooks': 'The webhook request was invalid.',
  'internal_server:webhooks': 'An internal error occurred while processing the webhook.',
  'not_found:webhooks': 'Webhook not found.',
  'bad_request:admin': 'The admin request was invalid.',
  'not_found:admin': 'Admin resource not found.',
  "bad_request:database": "The database request was invalid.",
  "bad_request:chat": "The chat request was invalid.",
  "bad_request:stream": "The stream request was invalid.",
  "bad_request:history": "The history request was invalid.",
  "bad_request:vote": "The vote request was invalid.",
  "bad_request:suggestions": "The suggestions request was invalid.",
  "unauthorized:stream": "You are not authorized to access this stream.",
  "unauthorized:database": "You are not authorized to access this database.",
  "unauthorized:history": "You are not authorized to access this history.",
  "unauthorized:suggestions": "You are not authorized to access these suggestions.",
  "unauthorized:user": "You are not authorized to perform this action on this user.",
  "unauthorized:admin": "You are not authorized to perform this admin action.",
  "unauthorized:email": "You are not authorized to perform this email action.",
  "unauthorized:snippets": "You are not authorized to access these snippets.",
  "unauthorized:subscription": "You are not authorized to access this subscription.",
  "unauthorized:webhooks": "You are not authorized to access this webhook.",
  "forbidden:stream": "You do not have permission to access this stream.",
  "forbidden:database": "You do not have permission to access this database.",
  "forbidden:history": "You do not have permission to access this history.",
  "forbidden:suggestions": "You do not have permission to access these suggestions.",
  "forbidden:user": "You do not have permission to perform this action on this user.",
  "forbidden:admin": "You do not have permission to perform this admin action.",
  "forbidden:email": "You do not have permission to perform this email action.",
  "forbidden:snippets": "You do not have permission to access these snippets.",
  "forbidden:subscription": "You do not have permission to access this subscription.",
  "forbidden:webhooks": "You do not have permission to access this webhook.",
  "not_found:stream": "Stream not found.",
  "not_found:database": "Database resource not found.",
  "not_found:history": "History not found.",
  "not_found:suggestions": "Suggestions not found.",
  "not_found:subscription": "Subscription not found.",
  "not_found:vote": "Vote target not found.",
  "rate_limit:stream": "You have exceeded the stream rate limit.",
  "rate_limit:database": "You have exceeded the database rate limit.",
  "rate_limit:history": "You have exceeded the history rate limit.",
  "rate_limit:vote": "You have exceeded the vote rate limit.",
  "rate_limit:document": "You have exceeded the document rate limit.",
  "rate_limit:suggestions": "You have exceeded the suggestions rate limit.",
  "rate_limit:user": "You have exceeded the user rate limit.",
  "rate_limit:admin": "You have exceeded the admin rate limit.",
  "rate_limit:email": "You have exceeded the email rate limit.",
  "rate_limit:snippets": "You have exceeded the snippets rate limit.",
  "rate_limit:subscription": "You have exceeded the subscription rate limit.",
  "rate_limit:webhooks": "You have exceeded the webhooks rate limit.",
  "rate_limit:auth": "You have exceeded the authentication rate limit.",
  "internal_server:suggestions": "An internal error occurred with the suggestions service.",
  "unauthorized:chat": "You are not authorized to access this chat.",
  "internal_server:document": "An internal error occurred with the document service.",
  "offline:suggestions": "Suggestions service is currently unavailable.",
  "quota:chat": "You have reached your chat quota limit.",
  "quota:auth": "You have reached your authentication quota limit.",
  "quota:api": "You have reached your API quota limit.",
  "quota:stream": "You have reached your streaming quota limit.",
  "quota:database": "You have reached your database quota limit.",
  "quota:history": "You have reached your history quota limit.",
  "quota:vote": "You have reached your voting quota limit.",
  "quota:document": "You have reached your document quota limit.",
  "quota:suggestions": "You have reached your suggestions quota limit.",
  "quota:user": "You have reached your user quota limit.",
  "quota:admin": "You have reached your admin quota limit.",
  "quota:email": "You have reached your email quota limit.",
  "quota:subscription": "You have reached your subscription quota limit.",
  "quota:webhooks": "You have reached your webhook quota limit.",
  
  // Models
  "bad_request:models": "The models request was invalid.",
  "unauthorized:models": "You are not authorized to access the models.",
  "forbidden:models": "You do not have permission to access the models.",
  "not_found:models": "Model not found.",
  "conflict:models": "A conflict occurred with the models request.",
  "rate_limit:models": "You have exceeded the models rate limit.",
  "quota:models": "You have reached your models quota limit.",
  "internal_server:models": "An internal error occurred with the models service.",
  "offline:models": "Models service is currently unavailable.",
  
  // Apps
  "bad_request:apps": "The app request was invalid.",
  "unauthorized:apps": "You are not authorized to manage apps.",
  "forbidden:apps": "You do not have permission to access this app.",
  "not_found:apps": "App not found.",
  "conflict:apps": "A app conflict occurred.",
  "rate_limit:apps": "You have exceeded the apps rate limit.",
  "quota:apps": "You have reached your apps quota limit.",
  "internal_server:apps": "An internal error occurred with the apps service.",
  "offline:apps": "Apps service is currently unavailable.",
  
  "bad_request:topup": "The top-up request was invalid.",
  "unauthorized:topup": "You are not authorized to purchase top-up credits.",
  "forbidden:topup": "You do not have permission to purchase top-up credits.",
  "not_found:topup": "Topup purchase not found.",
  "conflict:topup": "A top-up conflict occurred.",
  "rate_limit:topup": "You have exceeded the top-up rate limit.",
  "quota:topup": "You have reached your top-up quota limit.",
  "internal_server:topup": "An internal error occurred with the top-up service.",
  "offline:topup": "Topup service is currently unavailable."
};

export class FillAppError extends Error {
  public readonly type: ErrorType;
  public readonly surface: ErrorSurface | BillingErrorReason;
  public readonly statusCode: number;
  public readonly errorCode: ErrorCode;
  public override cause?: string;

  constructor(errorCode: ErrorCode, message?: string | null, cause?: string) {
    super(message || ERROR_MESSAGES[errorCode] || 'An unknown error occurred.');

    const [type, surfaceOrReason] = errorCode.split(':');

    this.errorCode = errorCode;
    this.type = type as ErrorType;
    this.cause = cause;
    this.surface = surfaceOrReason as ErrorSurface | BillingErrorReason;
    this.statusCode = getStatusCodeByType(this.type);
    Object.setPrototypeOf(this, new.target.prototype);
  }

  public toResponsePayload() {
    const visibility = getVisibility(this.surface);

    if (visibility === 'log') {
      return {
        code: 'internal_server:api',
        message: 'Something went wrong. Please try again later.',
      };
    }

    const payload: { code: ErrorCode; message: string; cause?: string } = {
      code: this.errorCode,
      message: this.message,
    };

    if (this.cause) {
      payload.cause = this.cause;
    }

    return payload;
  }
}

